import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import type { KhushiItineraryItem } from "../khushiData";
import { isItemRevealedFromItem } from "../khushiAccess";
import { KHUSHI_COLORS, KHUSHI_FONTS } from "../theme";

const { maroon, gold, goldSoft, ink } = KHUSHI_COLORS;
const { display: fontDisplay } = KHUSHI_FONTS;

const sxEventItem: Record<string, unknown> = {
  position: "relative",
  mb: 3.5,
  pl: 1,
  "&:last-of-type": { mb: 0 },
  "&::before": {
    content: '""',
    position: "absolute",
    left: "-1.05rem",
    top: "0.45rem",
    width: 9,
    height: 9,
    borderRadius: "50%",
    bgcolor: maroon,
    border: `2px solid ${gold}`,
    boxShadow: "0 0 0 3px rgba(212, 175, 55, 0.12)",
  },
};

const sxRedactedBlock: Record<string, unknown> = {
  position: "relative",
  display: "inline-block",
  width: "100%",
  maxWidth: "100%",
  my: "0.15rem",
  mb: "0.35rem",
};

const sxRedactedBlur: Record<string, unknown> = {
  filter: "blur(7px)",
  userSelect: "none",
  pointerEvents: "none",
  opacity: 0.55,
  transform: "translateZ(0)",
};

const sxRedactedMask: Record<string, unknown> = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  fontFamily: fontDisplay,
  fontSize: "0.85rem",
  letterSpacing: "0.25em",
  color: gold,
  textShadow: "0 0 20px rgba(0,0,0,0.8)",
};

const sxRevealUnblur: Record<string, unknown> = {
  animation: "khushi-reveal-unblur 900ms cubic-bezier(0.2, 0.75, 0.2, 1) both",
  "@keyframes khushi-reveal-unblur": {
    "0%": { filter: "blur(14px)", opacity: 0.15, transform: "translateY(6px)" },
    "55%": { filter: "blur(6px)", opacity: 0.85 },
    "100%": { filter: "blur(0px)", opacity: 1, transform: "translateY(0px)" },
  },
};

export function ItineraryEventItem({
  item,
  isPrivileged,
  isOwner,
  isAnimating,
}: {
  item: KhushiItineraryItem;
  isPrivileged: boolean;
  isOwner: boolean;
  isAnimating: boolean;
}) {
  const revealed = isPrivileged || isItemRevealedFromItem(item);

  return (
    <Box component="li" sx={sxEventItem}>
      <Typography
        sx={{
          fontFamily: fontDisplay,
          fontSize: "0.72rem",
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: gold,
          mb: 0.5,
        }}
      >
        {item.timeLabel}
      </Typography>

      {revealed ? (
        <Box sx={isAnimating ? sxRevealUnblur : undefined}>
          <Typography
            component="h2"
            sx={{
              fontSize: "1.35rem",
              fontWeight: 600,
              color: ink,
              m: 0,
              mb: 0.5,
            }}
          >
            {item.title}
          </Typography>

          {item.subtitle && (
            <Typography
              sx={{
                fontStyle: "italic",
                color: "rgba(248, 240, 230, 0.72)",
                m: 0,
                mb: 1,
                fontSize: "1.05rem",
              }}
            >
              {item.subtitle}
            </Typography>
          )}

          {item.notes && (
            <Typography
              sx={{
                m: 0,
                mb: 1.25,
                color: "rgba(248, 240, 230, 0.88)",
              }}
            >
              {item.notes}
            </Typography>
          )}

          {item.linkUrl && item.linkLabel && (
            <Link
              href={item.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: "inline-block",
                fontFamily: fontDisplay,
                fontSize: "0.72rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: gold,
                borderBottom: "1px solid rgba(212, 175, 55, 0.45)",
                pb: "2px",
                "&:hover": {
                  color: goldSoft,
                  borderBottomColor: goldSoft,
                },
              }}
            >
              {item.linkLabel}
            </Link>
          )}

          {isOwner && (item.ownerNotes || item.reservationUrl) && (
            <Box
              sx={{
                mt: 1.75,
                pt: 1.5,
                borderTop: "1px solid rgba(212, 175, 55, 0.2)",
              }}
            >
              <Typography
                sx={{
                  fontFamily: fontDisplay,
                  fontSize: "0.65rem",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: gold,
                  mb: 1,
                }}
              >
                Reservations & ops
              </Typography>

              {item.ownerNotes && (
                <Typography
                  sx={{
                    m: 0,
                    mb: 1,
                    fontSize: "0.95rem",
                    color: "rgba(248, 240, 230, 0.82)",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {item.ownerNotes}
                </Typography>
              )}

              {item.reservationUrl && (
                <Link
                  href={item.reservationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  sx={{
                    display: "inline-block",
                    fontFamily: fontDisplay,
                    fontSize: "0.68rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: goldSoft,
                    borderBottom: "1px solid rgba(232, 213, 163, 0.4)",
                    pb: "2px",
                    "&:hover": { color: gold },
                  }}
                >
                  Confirmation link
                </Link>
              )}
            </Box>
          )}
        </Box>
      ) : (
        <>
          <Box sx={sxRedactedBlock}>
            <Typography
              component="div"
              aria-hidden
              sx={{
                ...sxRedactedBlur,
                fontSize: "1.35rem",
                fontWeight: 600,
                color: ink,
                m: 0,
                mb: 0.5,
              }}
            >
              A surprise in the city
            </Typography>
            <Box sx={sxRedactedMask} aria-hidden>
              ···
            </Box>
          </Box>

          <Box sx={sxRedactedBlock}>
            <Typography
              aria-hidden
              sx={{
                ...sxRedactedBlur,
                fontStyle: "italic",
                color: "rgba(248, 240, 230, 0.72)",
                m: 0,
                mb: 1,
                fontSize: "1.05rem",
              }}
            >
              The map unfolds later
            </Typography>
            <Box sx={sxRedactedMask} aria-hidden>
              ···
            </Box>
          </Box>

          <Typography
            sx={{
              letterSpacing: "0.15em",
              color: "rgba(248,240,230,0.5)",
              m: 0,
              mb: 0,
            }}
          >
            ••••• •••• ••••••
          </Typography>
        </>
      )}
    </Box>
  );
}

